// src/hooks/useSessionTimeout.js
import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const TIMEOUT_MS    = 9 * 60 * 1000;  // 5 minutes
const WARNING_MS    = 4 * 60 * 1000;  // warn at 4 min (1 min before logout)

export const useSessionTimeout = () => {
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    toast.error('Session expired due to inactivity. Please log in again.');
    window.location.href = '/login-screen';
  }, []);

  const resetTimer = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    // Warn at 4 minutes
    warningRef.current = setTimeout(() => {
      toast('⚠️ Your session will expire in 1 minute due to inactivity.', {
        duration: 10000,
        icon: '⏱️',
      });
    }, WARNING_MS);

    // Logout at 5 minutes
    timeoutRef.current = setTimeout(() => {
      logout();
    }, TIMEOUT_MS);
  }, [logout]);

  useEffect(() => {
    // Events that count as "activity"
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer(); // start timer on mount

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [resetTimer]);
};